import {Record, Set} from 'immutable';

import {makeReducer, match} from 'web-app/util/redux';

import * as Actions from './actions';
import * as Types from './types';
import * as Constants from './constants';
import {removeDuplicatesFromObjectArrayByKey} from './helpers';

export class FamlyLiveState extends Record<{
    creatingMeeting: boolean;
    famlyMeeting?: Types.FamlyMeeting;
    isSharingLocalVideo: boolean;
    isMuted: boolean;
    tiles: {[boundAttendeeId: string]: Types.TileState};
    externalJoinUrl: string;
    refreshToken: string;
    attendeeIds: Set<string>;
    famlyMeetingId: string;
    attendeeInfo: {[loginId: string]: Types.Attendee};
    deviceNotFoundErrors: Types.DeviceNotFoundErrors;
    audioInputDeviceList: MediaDeviceInfo[];
    audioOutputDeviceList: MediaDeviceInfo[];
    videoInputDeviceList: MediaDeviceInfo[];
    selectedAudioInputDevice: MediaDeviceInfo | null;
    selectedAudioOutputDevice: MediaDeviceInfo | null;
    selectedVideoInputDevice: MediaDeviceInfo | null;
    meetingState: Types.MEETING_STATE;
    multipleTilesTrackingEventSent: boolean;
    attendeeVolumeInfo: {[boundAttendeeId: string]: Types.AttendeeVolumeInfo};
}>({
    creatingMeeting: false,
    famlyMeeting: undefined,
    isSharingLocalVideo: false,
    isMuted: false,
    tiles: {},
    externalJoinUrl: '',
    refreshToken: '',
    attendeeIds: Set<string>(),
    famlyMeetingId: '',
    attendeeInfo: {},
    deviceNotFoundErrors: {
        audioInput: false,
        audioOutput: false,
        videoInput: false,
    },
    audioInputDeviceList: [],
    audioOutputDeviceList: [],
    videoInputDeviceList: [],
    selectedAudioInputDevice: null,
    selectedAudioOutputDevice: null,
    selectedVideoInputDevice: null,
    meetingState: Types.MEETING_STATE.DEVICE_SUPPORTED,
    multipleTilesTrackingEventSent: false,
    attendeeVolumeInfo: {},
}) {}

export default makeReducer<FamlyLiveState>(
    [
        match(Actions.multipleTilesTrackingEventSent, state => state.merge({multipleTilesTrackingEventSent: true})),
        match(Actions.deviceNotFoundError, (state, {payload}) => {
            switch (payload.deviceType) {
                case Types.DEVICE_NOT_FOUND.AUDIO_INPUT:
                    return state.merge({deviceNotFoundErrors: {...state.deviceNotFoundErrors, audioInput: true}});
                case Types.DEVICE_NOT_FOUND.AUDIO_OUTPUT:
                    return state.merge({deviceNotFoundErrors: {...state.deviceNotFoundErrors, audioOutput: true}});
                case Types.DEVICE_NOT_FOUND.VIDEO_INPUT:
                    return state.merge({deviceNotFoundErrors: {...state.deviceNotFoundErrors, videoInput: true}});
                default:
                    return state;
            }
        }),
        match(Actions.createMeeting, state => state.merge({creatingMeeting: true})),
        match(Actions.createMeetingFailed, state => state.merge({creatingMeeting: false})),
        match(Actions.createMeetingSuccess, (state, {payload}) =>
            state.merge({creatingMeeting: false, famlyMeeting: payload.famlyMeeting}),
        ),
        match(Actions.joinMeeting, state => state.merge({meetingState: Types.MEETING_STATE.JOINING})),
        match(Actions.joinMeetingSuccess, (state, {payload}) =>
            state.merge({meetingState: Types.MEETING_STATE.ACTIVE, famlyMeetingId: payload.famlyMeetingId}),
        ),
        match(Actions.joinMeetingFailed, state => state.merge({meetingState: Types.MEETING_STATE.EXPIRED})),
        match(Actions.startLocalVideoSuccess, (state, {payload}) =>
            state.merge({isSharingLocalVideo: Boolean(payload.videoDevice)}),
        ),
        match(Actions.startLocalVideoFailed, state => state.merge({isSharingLocalVideo: false})),
        match(Actions.stopLocalVideoSuccess, state => state.merge({isSharingLocalVideo: false})),
        match(Actions.muteLocalAudioSuccess, state => state.merge({isMuted: true})),
        match(Actions.muteLocalAudioFailed, state => state.merge({isMuted: false})),
        match(Actions.unmuteLocalAudioSuccess, state => state.merge({isMuted: false})),
        match(Actions.unmuteLocalAudioFailed, state => state.merge({isMuted: true})),
        match(Actions.setRefreshToken, (state, {payload}) => state.merge({refreshToken: payload.refreshToken})),
        match(Actions.getMeetingSuccess, (state, {payload}) => {
            const {famlyMeeting} = payload;
            const {attendees, attendeeLoginIdMap} = famlyMeeting;
            const {attendeeIds, tiles} = state;

            const updatedAttendeeInfo = attendees.reduce((obj, attendee) => {
                obj[attendee.loginId] = attendee;
                return obj;
            }, {});

            // If we have an attendee with no tile -> Add an empty tile for that attendee
            // This can happen when a user doesn't allow camera access but still has audio.
            const emptyTiles = attendeeIds.reduce((obj, attendeeId) => {
                if (!tiles[attendeeId]) {
                    obj[attendeeId] = {
                        ...Constants.EMPTY_TILE,
                        boundExternalUserId: attendeeLoginIdMap[attendeeId], // Same as loginId, necessary to get attendeeInfo
                        boundAttendeeId: attendeeId, // These are the IDs used by the Chime SDK
                        active: false,
                    };
                }
                return obj;
            }, {});

            return state.merge({
                famlyMeeting: payload.famlyMeeting,
                attendeeInfo: updatedAttendeeInfo,
                tiles: {...state.tiles, ...emptyTiles},
            });
        }),
        match(Actions.getJoinToken, state =>
            state.merge({meetingState: Types.MEETING_STATE.FETCHING_EXTERNAL_JOIN_URL}),
        ),
        match(Actions.getJoinTokenSuccess, (state, {payload}) =>
            state.merge({
                externalJoinUrl: payload.externalJoinUrl,
                meetingState: Types.MEETING_STATE.READY_TO_JOIN,
            }),
        ),
        match(Actions.getJoinTokenFailed, state => state.merge({meetingState: Types.MEETING_STATE.EXPIRED})),
        match(Actions.reset, () => new FamlyLiveState()),
        match(Actions.addAttendeeId, (state, {payload}) => {
            return state.merge({attendeeIds: state.attendeeIds.add(payload.attendeeId)});
        }),
        match(Actions.removeAttendeeId, (state, {payload}) => {
            const {[payload.attendeeId]: tileToBeRemoved, ...remainingTiles} = state.tiles;
            return state.merge({
                tiles: {...remainingTiles},
                attendeeIds: state.attendeeIds.delete(payload.attendeeId),
            });
        }),
        match(Actions.leaveMeetingSuccess, () => new FamlyLiveState()),
        match(Actions.tileDidUpdate, (state, {payload}) => {
            const tileState = payload.tile;
            const boundAttendeeId = tileState.boundAttendeeId;
            // This observer is always called twice when an attendee enables
            // the camera for some reason 🤷‍♂️
            if (!boundAttendeeId) {
                return state;
            }

            const updatedTiles = {
                ...state.tiles,
                [boundAttendeeId]: tileState,
            };
            return state.merge({tiles: updatedTiles});
        }),
        match(Actions.tileDeleted, (state, {payload}) => {
            // This action is called when an attendee disables their video feed
            const {tileId} = payload;
            const {tiles} = state;
            const boundAttendeeIdToUpdate = Object.keys(tiles).find(boundAttendeeId => {
                const tile = tiles[boundAttendeeId];
                return tile.tileId && tile.tileId === tileId;
            });
            if (!boundAttendeeIdToUpdate) {
                return state;
            }

            const tileToUpdate = tiles[boundAttendeeIdToUpdate];
            tileToUpdate.active = false;
            return state.merge({
                tiles: {
                    [boundAttendeeIdToUpdate]: tileToUpdate,
                    ...tiles,
                },
            });
        }),
        match(Actions.updateAudioInputDeviceList, (state, {payload}) =>
            state.merge({
                audioInputDeviceList: removeDuplicatesFromObjectArrayByKey<MediaDeviceInfo>(
                    payload.audioInputDeviceList,
                    'groupId',
                ),
            }),
        ),
        match(Actions.updateAudioOutputDeviceList, (state, {payload}) =>
            state.merge({
                audioOutputDeviceList: removeDuplicatesFromObjectArrayByKey<MediaDeviceInfo>(
                    payload.audioOutputDeviceList,
                    'groupId',
                ),
            }),
        ),
        match(Actions.updateVideoInputDeviceList, (state, {payload}) =>
            state.merge({videoInputDeviceList: payload.videoInputDeviceList}),
        ),
        match(Actions.setSelectedAudioInputDeviceSuccess, (state, {payload}) =>
            state.merge({selectedAudioInputDevice: payload.audioInputDevice}),
        ),
        match(Actions.setSelectedAudioOutputDeviceSuccess, (state, {payload}) =>
            state.merge({selectedAudioOutputDevice: payload.audioOutputDevice}),
        ),
        match(Actions.setSelectedVideoInputDeviceSuccess, (state, {payload}) =>
            state.merge({selectedVideoInputDevice: payload.videoInputDevice}),
        ),
        match(Actions.attendeeVolumeInfoDidChange, (state, {payload}) => {
            const {boundAttendeeId, volume, muted, signalStrength} = payload;
            const currentAttendeeVolumeInfo = state.attendeeVolumeInfo[boundAttendeeId];
            // A null value for any field means that it has not changed.
            const updatedAttendeeVolumeInfo: Types.AttendeeVolumeInfo = {
                boundAttendeeId,
                volume: volume || currentAttendeeVolumeInfo?.volume,
                muted: Boolean(muted),
                signalStrength: signalStrength || currentAttendeeVolumeInfo?.signalStrength,
            };
            return state.merge({
                attendeeVolumeInfo: {
                    ...state.attendeeVolumeInfo,
                    [boundAttendeeId]: updatedAttendeeVolumeInfo,
                },
            });
        }),
        match(Actions.removeAttendeeVolumeInfo, (state, {payload}) => {
            const {boundAttendeeId} = payload;
            const {[boundAttendeeId]: attendeeVolumeInfoToBeRemoved, ...remainingAttendeeVolumeInfo} =
                state.attendeeVolumeInfo;
            return state.merge({attendeeVolumeInfo: remainingAttendeeVolumeInfo});
        }),
    ],
    new FamlyLiveState(),
);
